import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import useOutsideClick from '../hooks/useOutsideClick';
import { applyDocumentDirection } from '../i18n/direction';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
  { code: 'he', label: 'עברית' },
];

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useOutsideClick(containerRef, () => setIsOpen(false), isOpen);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    applyDocumentDirection(lang);
    setIsOpen(false);
  };

  const currentLanguage =
    LANGUAGES.find((l) => l.code === i18n.language)?.label || 'English';

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        className="header-icon-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('header.language', 'Language')}
        title={currentLanguage}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '36px',
          width: '36px',
          minHeight: '36px',
          padding: 0,
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          color: 'var(--color-text-secondary, #64748B)',
          transition: 'background-color 150ms ease, color 150ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)';
          e.currentTarget.style.color = 'var(--color-text-primary, #1E293B)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'var(--color-text-secondary, #64748B)';
        }}
      >
        <Globe size={20} strokeWidth={2} aria-hidden="true" focusable="false" />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label={t('header.language', 'Language')}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            insetInlineEnd: 0,
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
            minWidth: '160px',
            zIndex: 60,
            overflow: 'hidden',
          }}
        >
          {LANGUAGES.map((lang) => {
            const isActive = i18n.language === lang.code;
            return (
              <button
                key={lang.code}
                role="option"
                aria-selected={isActive}
                onClick={() => handleLanguageChange(lang.code)}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  backgroundColor: isActive ? 'var(--color-surface-alt)' : 'transparent',
                  color: isActive
                    ? 'var(--color-primary-dark)'
                    : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  textAlign: 'start',
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 400,
                  transition: 'background-color 150ms ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {lang.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
